import React, { useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import ReactCountryFlag from 'react-country-flag'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap'

import { queries } from '~/constants/mediaQuery'
import LocastyLogo from '~/assets/img/logo/locasty-text.svg'
import BgPattern from '../../../assets/img/invite/bg-pattern.png'
import AuthSeal from '../../../assets/img/invite/auth-seal.png'
import { IntlContext } from '~/utility/context/Internationalization'
import { ChevronDown, ChevronUp } from 'react-feather'

export const AuthLayout = ({
  children,
  title,
  description,
  descriptionValues = [],
}) => {
  const [langDropdown, setLangDropdown] = useState(false)

  const handleLangDropdown = () => setLangDropdown(prevState => !prevState)

  return (
    <>
      <Main>
        <div className="auth-form">
          <div className="auth-logo">
            <a href="/">
              <img className="logo" src={LocastyLogo} alt="" />
            </a>
          </div>
          <div className="auth-header">
            <h1>
              <FormattedMessage id={title} />
            </h1>
            {description && (
              <h2>
                <FormattedMessage id={description} values={descriptionValues} />
              </h2>
            )}
          </div>
          {children}
        </div>
        <div className="auth-background">
          <img className="pattern" src={BgPattern} alt="" />
          <img className="seal" src={AuthSeal} alt="" />
        </div>
      </Main>
      <Footer>
        <div className="border-top-div"></div>
        <div className="copyright-div">
          <div>Copyright &copy; {new Date().getFullYear()}</div>
          <div>
            <FormattedMessage id="All rights reserved" />
          </div>
        </div>
        <div>
          <IntlContext.Consumer>
            {context => {
              const langArr = {
                en: 'English',
                br: 'Português',
              }
              return (
                <Dropdown
                  tag="li"
                  className="dropdown-language nav-item ml-1"
                  isOpen={langDropdown}
                  style={{ listStyle: 'none' }}
                  toggle={handleLangDropdown}
                  data-tour="language"
                >
                  <DropdownToggle tag="a" className="nav-link">
                    <ReactCountryFlag
                      className="country-flag"
                      countryCode={
                        context?.state?.locale === 'en'
                          ? 'us'
                          : context?.state?.locale
                      }
                      svg
                    />
                    <span className="d-sm-inline-block text-capitalize align-middle ml-50">
                      {langArr[context?.state?.locale]}
                    </span>
                    {langDropdown ? (
                      <ChevronDown size={16} color="rgba(0, 0, 0, 0.64)" />
                    ) : (
                      <ChevronUp size={16} color="rgba(0, 0, 0, 0.64)" />
                    )}
                  </DropdownToggle>
                  <DropdownMenu right>
                    <DropdownItem
                      tag="a"
                      onClick={() => context.switchLanguage('en')}
                    >
                      <ReactCountryFlag
                        className="country-flag"
                        countryCode="us"
                        svg
                      />
                      <span className="ml-1">English</span>
                    </DropdownItem>
                    <DropdownItem
                      tag="a"
                      onClick={() => context.switchLanguage('br')}
                    >
                      <ReactCountryFlag
                        className="country-flag"
                        countryCode="br"
                        svg
                      />
                      <span className="ml-1">Português</span>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              )
            }}
          </IntlContext.Consumer>
        </div>
      </Footer>
    </>
  )
}

const Footer = styled.footer`
  display: flex;
  height: 5rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 2rem;
  background-color: #fafbfd;
  border-top: 1px solid #e2e8f0;
  color: #a0aec0;
  font-weight: 700;

  .copyright-div {
    width: 100%;
    display: flex;
    gap: 1em;

    @media (${queries.less.mds}) {
      justify-content: space-between;
    }
  }

  .border-top-div {
    display: none;
    width: 100%;
    min-height: 1px;
    background-color: #e2e8f0;

    @media (${queries.less.mds}) {
      display: flex;
    }
  }

  a.nav-link {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 9999px;
    padding: 0.25rem 0.5rem;
    gap: 1.5rem;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.36);
  }

  @media (${queries.less.mds}) {
    flex-direction: column;
    justify-content: center;
    padding: 1.5rem;
    height: 8rem;
    border-top: none;
  }
`

const Main = styled.div`
  display: flex;
  justify-content: center;
  height: calc(100vh - 5rem);
  background-color: #fafbfd;

  @media (${queries.less.mds}) {
    height: calc(100vh - 8rem);
  }

  .auth-form {
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3rem;
    padding: 7.5rem 2rem 7.5rem 7rem;

    @media (${queries.less.mds}) {
      width: 100%;
      padding: 1.5rem;
    }

    .auth-logo {
      display: flex;
      flex-direction: column;
      align-items: start;
      min-width: 30.5625rem;

      @media (${queries.less.mds}) {
        min-width: 100%;
      }
    }

    .auth-header {
      display: flex;
      flex-direction: column;
      align-items: start;
      min-width: 30.5625rem;

      @media (${queries.less.mds}) {
        min-width: 100%;
      }
    }

    h1 {
      max-width: 30.5625rem;
      color: #1a202c;
      font-size: 2.25rem;
      font-style: normal;
      font-weight: 500;
      line-height: 2.5rem;
    }

    h2 {
      max-width: 30.5625rem;
      color: #4a5568;
      font-size: 1rem;
      font-style: normal;
      font-weight: 400;
      line-height: 1.5rem;
    }
  }

  .auth-background {
    position: relative;
    width: 50%;
    background-color: #7367f0;
    overflow: hidden;

    @media (${queries.less.mds}) {
      display: none;
    }
  }

  .pattern {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .seal {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: 19%;
    margin-top: 5%;

    transform: translate(-50%, -50%);
  }
`
